import { useMemo, useState } from "react";
import { Loader2 } from "@fg/icons";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { browserTimeZone, offsetLabel, parseClock, timeZoneOptions } from "@/lib/timezone";
import { usePutMailCalendarSettings, type MailCalendarSettings } from "./mailCalendarApi";

const WEEKDAYS: Array<[number, string]> = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];

/**
 * Working hours and time zone — the same per-user calendar settings as Mail's
 * (stored on the Mail BFF), so both calendars and the scheduling assistant agree.
 */
export function WorkingHoursDialog({ open, onOpenChange, settings }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    settings: MailCalendarSettings | undefined;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Working hours and time zone</DialogTitle>
                    <DialogDescription>
                        Colleagues scheduling with you see these hours, and your calendar shows times in this zone. Shared with Mail.
                    </DialogDescription>
                </DialogHeader>
                {settings ? (
                    <Form key={`${settings.workDays.join(",")}|${settings.workStart}|${settings.workEnd}|${settings.timeZone}`} settings={settings} onDone={() => onOpenChange(false)} />
                ) : (
                    <p className="text-sm text-muted-foreground">Calendar settings couldn't be loaded from Mail.</p>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Form({ settings, onDone }: { settings: MailCalendarSettings; onDone: () => void }) {
    const put = usePutMailCalendarSettings();
    const [days, setDays] = useState<number[]>(settings.workDays);
    const [start, setStart] = useState(settings.workStart);
    const [end, setEnd] = useState(settings.workEnd);
    const [tz, setTz] = useState(settings.timeZone);
    const zones = useMemo(() => {
        const all = timeZoneOptions();
        return all.includes(tz) ? all : [tz, ...all];
    }, [tz]);
    const deviceZone = browserTimeZone();
    const s = parseClock(start);
    const e = parseClock(end);
    const invalid = days.length === 0 || s === null || e === null || e <= s;

    return (
        <form
            className="space-y-3"
            onSubmit={(ev) => {
                ev.preventDefault();
                if (invalid) return;
                put.mutate({ workDays: days, workStart: start, workEnd: end, timeZone: tz }, { onSuccess: onDone });
            }}
        >
            <div role="group" aria-label="Working days" className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map(([d, label]) => {
                    const on = days.includes(d);
                    return (
                        <button
                            key={d}
                            type="button"
                            aria-pressed={on}
                            onClick={() => setDays((prev) => (on ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)))}
                            className={cn(
                                "h-8 w-11 rounded-md border text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                            )}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
            <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-muted-foreground">
                    Start
                    <input type="time" value={start} onChange={(ev) => setStart(ev.target.value)}
                        className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
                </label>
                <label className="text-xs text-muted-foreground">
                    End
                    <input type="time" value={end} onChange={(ev) => setEnd(ev.target.value)}
                        className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground" />
                </label>
            </div>
            <label className="block text-xs text-muted-foreground">
                Time zone
                <select value={tz} onChange={(ev) => setTz(ev.target.value)}
                    className="mt-1 block h-9 w-full rounded-md border bg-background px-2 text-sm text-foreground">
                    {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")} ({offsetLabel(z)})</option>)}
                </select>
            </label>
            {deviceZone !== tz ? (
                <button type="button" onClick={() => setTz(deviceZone)} className="text-xs text-primary underline-offset-2 hover:underline">
                    Use this device's zone ({deviceZone})
                </button>
            ) : null}
            {invalid ? <p className="text-xs text-destructive">Pick at least one day, and an end after the start.</p> : null}
            {put.isError ? <p className="text-xs text-destructive" role="alert">{put.error.message}</p> : null}
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onDone} className="h-9 rounded-md border px-4 text-sm hover:bg-accent">Cancel</button>
                <button
                    type="submit"
                    disabled={invalid || put.isPending}
                    className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                    {put.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
                </button>
            </div>
        </form>
    );
}
